// Test-takers' passkeys in the browser (Face ID, Touch ID, a security key). The options are
// fetched ahead of the tap: Safari only shows the passkey sheet when it opens straight from
// the tap, not after a network wait. Each challenge works once and for a few minutes.

import { WebAuthnError, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { api, type CodeInput, type PersonPasskey, type SignInStep } from "./api";

const TTL_MS = 4 * 60_000;
let signInReady: { options: PublicKeyCredentialRequestOptionsJSON; at: number } | null = null;
let addReady: { options: PublicKeyCredentialCreationOptionsJSON; at: number } | null = null;

export class PasskeyError extends Error {}

const codeOf = (err: unknown): string => (err instanceof WebAuthnError ? err.code : err instanceof Error ? err.name : "");
const cancelled = (err: unknown) => ["ERROR_CEREMONY_ABORTED", "AbortError", "NotAllowedError"].includes(codeOf(err));

/** Whether this browser can use passkeys at all. */
export const passkeysSupported = (): boolean => typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";

export async function prepareSignIn(): Promise<void> {
  if (signInReady && Date.now() - signInReady.at < TTL_MS) return;
  signInReady = { options: (await api.passkeySignInOptions()) as unknown as PublicKeyCredentialRequestOptionsJSON, at: Date.now() };
}

/** Gets the add options ready. With the authenticator app on, this is where the code goes. */
export async function prepareAdd(input?: CodeInput): Promise<void> {
  if (!input && addReady && Date.now() - addReady.at < TTL_MS) return;
  addReady = { options: (await api.passkeyAddOptions(input)) as unknown as PublicKeyCredentialCreationOptionsJSON, at: Date.now() };
}

/** Signs in with a passkey this device offers (or, a touch-only key with the app on, on to the code step). */
export async function signInWithPasskey(): Promise<SignInStep> {
  const ready = signInReady && Date.now() - signInReady.at < TTL_MS ? signInReady.options : null;
  signInReady = null;
  const options = ready ?? ((await api.passkeySignInOptions()) as unknown as PublicKeyCredentialRequestOptionsJSON);
  let response;
  try {
    response = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    throw new PasskeyError(cancelled(err) ? "Passkey sign-in was cancelled." : "Your device couldn't use a passkey here. Sign in another way.");
  }
  return api.signInWithPasskeyResponse(response);
}

/** A name for the passkey list: "iPhone", "Mac", "Android phone"… */
export function deviceName(ua = typeof navigator === "undefined" ? "" : navigator.userAgent): string {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows computer";
  return "This device";
}

/** Adds a passkey for the signed-in person. */
export async function addPasskey(): Promise<PersonPasskey> {
  const ready = addReady && Date.now() - addReady.at < TTL_MS ? addReady.options : null;
  addReady = null;
  const options = ready ?? ((await api.passkeyAddOptions()) as unknown as PublicKeyCredentialCreationOptionsJSON);
  let response;
  try {
    response = await startRegistration({ optionsJSON: options });
  } catch (err) {
    if (["ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED", "InvalidStateError"].includes(codeOf(err))) {
      throw new PasskeyError("This device already has a passkey for your profiles.");
    }
    throw new PasskeyError(cancelled(err) ? "The passkey wasn't saved (it was cancelled, or your device didn't allow it)." : "Your device couldn't make a passkey here.");
  }
  return api.addPasskeyResponse(response, deviceName());
}
